import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { extractNumericReferenceRange } from '../../utils/observation';
import { ChartDurationToggle } from '../charts/ChartDurationToggle';
import { getChartDurationCutoff, type ChartDuration } from '../charts/chartDuration';
import { SeriesChart } from '../charts/SeriesChart';
import type { ChartConfig } from '../ui/chart';

export type LabTrendChartProps = {
  results: WithId<Observation>[];
};

const computeYDomain = (
  points: { y: number }[],
  refRange: { low: number | null; high: number | null } | null,
): [number, number] => {
  const values = points.map((p) => p.y);
  if (refRange?.low !== null && refRange?.low !== undefined) values.push(refRange.low);
  if (refRange?.high !== null && refRange?.high !== undefined) values.push(refRange.high);

  if (values.length === 0) return [0, 100];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || max * 0.2 || 10;
  const padding = range * 0.15;

  const step = range < 50 ? 5 : range < 200 ? 10 : 50;

  // Clamp at 0 — lab values are non-negative for all panels we currently display.
  // If we add panels with legitimately negative values, remove the Math.max(0, …).
  return [Math.max(0, Math.floor((min - padding) / step) * step), Math.ceil((max + padding) / step) * step];
};

const formatTimestamp = (value: number): string => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : format(date, DATE_FORMATS.MONTH_YEAR);
};

const formatTooltipDate = (value: unknown): string => {
  const date = new Date(value as number);
  return isNaN(date.getTime()) ? String(value) : format(date, DATE_FORMATS.DATE);
};

const parseNumericValue = (observation: Observation): number | null => {
  const value = observation.valueQuantity?.value;
  return value !== undefined ? value : null;
};

export const LabTrendChart = ({ results }: LabTrendChartProps): React.ReactNode => {
  const { t } = useTranslation('records');
  const [duration, setDuration] = useState<ChartDuration>('all');

  const chartConfig = useMemo<ChartConfig>(() => ({ y: { label: t('value'), color: 'var(--color-primary)' } }), [t]);

  const referenceRange = useMemo(() => {
    for (const result of results) {
      const range = extractNumericReferenceRange(result);
      if (range) return range;
    }
    return null;
  }, [results]);

  // All numeric points regardless of duration — used to decide whether to render at all
  const allPoints = useMemo(
    () =>
      results
        .filter((r) => r.effectiveDateTime && parseNumericValue(r) !== null)
        .map((r) => ({ x: r.effectiveDateTime!, y: parseNumericValue(r)! }))
        .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()),
    [results],
  );

  const cutoff = useMemo(() => getChartDurationCutoff(duration), [duration]);

  const points = useMemo(() => {
    if (!cutoff) return allPoints;
    return allPoints.filter((p) => new Date(p.x) >= cutoff);
  }, [allPoints, cutoff]);

  const yDomain = useMemo(() => computeYDomain(points, referenceRange), [points, referenceRange]);

  // Not enough data across all time — don't render the chart at all
  if (allPoints.length < 2) return null;

  const unit = results[0]?.valueQuantity?.unit ?? '';

  const xDomain: [number, number] = [
    cutoff?.getTime() ?? (points[0] ? new Date(points[0].x).getTime() : Date.now()),
    Date.now(),
  ];

  const formattedData: { x: number; y: number | null }[] = points.map((point) => ({
    x: new Date(point.x).getTime(),
    y: point.y,
  }));

  // Ensure boundary points exist so the X-axis renders ticks even with no data
  if (formattedData.length === 0 || formattedData[0]!.x > xDomain[0]) {
    formattedData.unshift({ x: xDomain[0], y: null });
  }
  if (formattedData[formattedData.length - 1]!.x < xDomain[1]) {
    formattedData.push({ x: xDomain[1], y: null });
  }

  return (
    <div className="mb-8 w-full" aria-label="Lab trend chart">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground">{t('trend')}</h2>
        <ChartDurationToggle value={duration} onChange={setDuration} />
      </div>
      <SeriesChart
        data={formattedData}
        config={chartConfig}
        xAxis={{ type: 'number', scale: 'time', domain: xDomain, tickFormatter: formatTimestamp }}
        yAxis={{ domain: yDomain, label: unit }}
        tooltipLabelFormatter={formatTooltipDate}
        referenceRange={
          referenceRange
            ? { low: referenceRange.low, high: referenceRange.high, label: t('referenceRangeLabel') }
            : undefined
        }
      />
    </div>
  );
};
